import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { clearUserCache } from '@/lib/offlineStorage';
import type { User } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // 1. Set up listener FIRST to catch events during getSession
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Don't null-out user on transient events like TOKEN_REFRESHED failures
        // Only clear user on explicit SIGNED_OUT
        if (event === 'SIGNED_OUT') {
          if (session === null && user) clearUserCache(user.id);
          setUser(null);
          setLoading(false);
        } else if (session?.user) {
          setUser(session.user);
          setLoading(false);
        }
        // For other events without a session (e.g. TOKEN_REFRESHED failure),
        // keep the current user state to avoid flicker
      }
    );

    // 2. Then restore session from storage
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { user, loading, signOut };
}
